import React, { useEffect, useState, useMemo } from "react";
import { sendApiRequest, sendWhatsAppNotification } from "../utils";
import VehicleTable from "./VehicleTable";
import { FaSearch, FaSort, FaSortUp, FaSortDown, FaDownload, FaFilter, FaTimes, FaSync, FaFilePdf, FaFileCsv } from "react-icons/fa";
import { toast } from "react-toastify";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useSelector } from "react-redux";

const TruckList = () => {
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "vehicleNumber", direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedTrucks, setSelectedTrucks] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const { user, isAuthenticated } = useSelector((state) => state.user);
  const [filters, setFilters] = useState({
    vehicleNumber: "",
    fullAddress: "",
    makerModel: "",
    engineNo: "",
    ownerName: "",
    registeredRTO: ""
  });

  const filterFields = [
    { key: 'vehicleNumber', label: 'Vehicle Number' },
    { key: 'fullAddress', label: 'Full Address' },
    { key: 'makerModel', label: 'Maker Model' },
    { key: 'engineNo', label: 'Engine Number' },
    { key: 'ownerName', label: "Owner's Name" },
    { key: 'registeredRTO', label: 'Registered RTO' }
  ];

  useEffect(() => {
    fetchTrucks();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportDropdown && !event.target.closest('.export-dropdown')) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportDropdown]);

  const fetchTrucks = async () => {
    setLoading(true);
    try {
      const response = await sendApiRequest("api/trucks/get", {}, 'GET');
      setTrucks(response.data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error fetching trucks");
      console.error("Error fetching trucks:", error);
    } finally {
      setLoading(false);
    }
  };




  const getExportData = () => {
    if (selectedTrucks.length === 0) {
      toast.warning("Please select trucks to export");
      return null;
    }

    // Create PDF in landscape mode with A2 size
    const doc = new jsPDF('landscape', 'mm', 'a2'); // Increased to A2 for more space

    // Set title with a background color and border
    doc.setFillColor(44, 62, 80); // Dark blue background
    doc.rect(0, 0, doc.internal.pageSize.width, 40, 'F'); // Title background
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(28);
    doc.setTextColor(255, 255, 255); // White text
    doc.text('Selected Trucks Report', doc.internal.pageSize.width / 2, 25, { align: 'center' });

    // Add a subtitle
    doc.setFontSize(14);
    doc.setTextColor(200, 200, 200); // Light gray text
    doc.text('Detailed report of selected trucks with images', doc.internal.pageSize.width / 2, 35, { align: 'center' });

    // Define table headers
    const tableColumn = [
      'ID', 'Vehicle Number', 'Full Address', 'Engine No', 'Chassis No', 'Owner Name',
      'Owner Phone', 'Owner Aadhar', 'Owner PAN', 'Bulk Data',
      'Driver Name', 'Driver Phone', 'License No', 'Aadhar No', 'Reference 1',
      'Reference 2', 'Status'
    ];

    // Image keys
    const imageKeys = ["ownerAadharImage", "ownerPanImage", "rcImage", "licenseImage", "aadharImage"];

    // Prepare table rows
    const tableRows = selectedTrucks.map((id) => {
      const truck = trucks.find((t) => t._id === id);
      if (!truck) return null;

      return [
        truck._id,
        truck.vehicleNumber,
        truck.fullAddress,
        truck.engineNo,
        truck.ChassisNo,
        truck.ownerName,
        truck.ownerPhoneNumber,
        truck.ownerAadharNumber,
        truck.ownerPanNumber,
        JSON.stringify(truck.bulkData), // Convert object to string
        truck.driverName,
        `${truck.driverPrimaryNumber}\n${truck.driverAlternativeNumber}`,
        truck.licenseNumber,
        truck.aadharNumber,
        `${truck.reference1Name}\n${truck.reference1PhoneNumber}`,
        `${truck.reference2Name}\n${truck.reference2PhoneNumber}`,
        truck.status
      ];
    }).filter(Boolean); // Remove null values

    // Generate table with adjusted styles
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 50, // Start below the title
      theme: 'striped',
      styles: {
        fontSize: 10,
        cellPadding: 4,
        overflow: 'linebreak',
        halign: 'center',
        valign: 'middle',
        lineColor: [44, 62, 80], // Border color
        lineWidth: 0.1
      },
      headStyles: {
        fillColor: [44, 62, 80],
        textColor: 255,
        fontSize: 12,
        fontStyle: 'bold',
        halign: 'center',
        cellPadding: 6
      },
      bodyStyles: {
        textColor: [40, 40, 40],
        fontSize: 10,
        cellPadding: 4
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { top: 50 },
      tableWidth: 'auto',
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 'auto' },
        4: { cellWidth: 'auto' },
        5: { cellWidth: 'auto' },
        6: { cellWidth: 'auto' },
        7: { cellWidth: 'auto' },
        8: { cellWidth: 'auto' },
        9: { cellWidth: 'auto' },
        10: { cellWidth: 'auto' },
        11: { cellWidth: 'auto' },
        12: { cellWidth: 'auto' },
        13: { cellWidth: 'auto' },
        14: { cellWidth: 'auto' },
        15: { cellWidth: 'auto' },
        16: { cellWidth: 'auto' }
      }
    });

    let yPosition = doc.lastAutoTable.finalY + 20; // Start images below table

    // Loop through each truck and add images below the table
    selectedTrucks.forEach((id, index) => {
      const truck = trucks.find((t) => t._id === id);
      if (!truck) return;

      // Add a section header for each truck
      doc.setFontSize(20);
      doc.setTextColor(44, 62, 80); // Dark blue text
      doc.text(`Images for Vehicle: ${truck.vehicleNumber || "N/A"}`, 20, yPosition);
      yPosition += 10;

      // Add a horizontal line below the section header
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(20, yPosition, doc.internal.pageSize.width - 20, yPosition);
      yPosition += 15;

      imageKeys.forEach((key) => {
        if (truck[key]) {
          try {
            // Label the image
            doc.setFontSize(14);
            doc.setTextColor(44, 62, 80); // Dark blue text
            doc.text(key.replace(/([A-Z])/g, " $1").trim(), 20, yPosition);

            // Add image (100x80 mm)
            doc.addImage(truck[key], "JPEG", 20, yPosition + 5, 100, 80);

            yPosition += 90; // Move down for next image

            if (yPosition > doc.internal.pageSize.height - 50) {
              doc.addPage();
              yPosition = 30;
            }
          } catch (error) {
            console.error(`Error adding image for ${key}:`, error);
          }
        }
      });

      if (index < selectedTrucks.length - 1) {
        doc.addPage(); // Separate each truck with a new page
        yPosition = 30;
      }
    });

    // Save the PDF
    toast.success("PDF export successful!");
    doc.save('Selected_Trucks_Report.pdf');
  };



  const exportToCSV = async () => {
    const exportData = selectedTrucks.map(id => {
        const truck = trucks.find(t => t._id === id);
        if (!truck) return null;

        // Extract required keys
        return {
            _id: truck._id || "N/A",
            vehicleNumber: truck.vehicleNumber || "N/A",
            fullAddress: truck.fullAddress || "N/A",
            engineNo: truck.engineNo || "N/A",
            ChassisNo: truck.chassisNo || "N/A",
            ownerName: truck.ownerName || "N/A",
            ownerPhoneNumber: truck.ownerPhoneNumber || "N/A",
            ownerAadharNumber: truck.ownerAadharNumber || "N/A",
            ownerAadharImage: truck.ownerAadharImage ? "[Image]" : "N/A",
            ownerPanNumber: truck.ownerPanNumber || "N/A",
            ownerPanImage: truck.ownerPanImage ? "[Image]" : "N/A",
            bulkData: truck.bulkData ? JSON.stringify(truck.bulkData) : "N/A",
            rcImage: truck.rcImage ? "[Image]" : "N/A",
            driverName: truck.driverName || "N/A",
            driverPrimaryNumber: truck.driverPrimaryNumber || "N/A",
            driverAlternativeNumber: truck.driverAlternativeNumber || "N/A",
            licenseNumber: truck.licenseNumber || "N/A",
            licenseImage: truck.licenseImage ? "[Image]" : "N/A",
            aadharNumber: truck.aadharNumber || "N/A",
            aadharImage: truck.aadharImage ? "[Image]" : "N/A",
            reference1Name: truck.reference1Name || "N/A",
            reference1PhoneNumber: truck.reference1PhoneNumber || "N/A",
            reference2Name: truck.reference2Name || "N/A",
            reference2PhoneNumber: truck.reference2PhoneNumber || "N/A",
            __v: truck.__v || "N/A"
        };
    }).filter(Boolean); // Remove null values

    if (exportData.length === 0) {
        toast.warning("No trucks selected for export.");
        return;
    }

    try {
        const headers = Object.keys(exportData[0]); // Get column names
        const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + 
            headers.join(",") + "\n" +
            exportData.map(row =>
                headers.map(key => `"${String(row[key]).replace(/"/g, '""')}"`).join(",")
            ).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        const fileName = `trucks_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Send WhatsApp notification

        toast.success("CSV export successful!");
    } catch (error) {
        console.error("CSV Export Error:", error);
        toast.error("Failed to export CSV. Please try again.");
    }
};




  const exportToPDF = async () => {
    getExportData();
  };

  const ExportDropdown = () => {
    return (
      <div className="relative export-dropdown">
        <button
          onClick={() => setShowExportDropdown(!showExportDropdown)}
          className={`flex items-center gap-2 px-4 py-2 ${selectedTrucks.length === 0
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-green-600 hover:bg-green-700'
            } text-white rounded-lg transition-all duration-200`}
          disabled={selectedTrucks.length === 0}
        >
          <FaDownload /> Export Selected ({selectedTrucks.length})
        </button>

        {showExportDropdown && selectedTrucks.length > 0 && (
          <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
            <div className="py-1" role="menu">
              <button
                onClick={() => {
                  exportToCSV();
                  setShowExportDropdown(false);
                }}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                role="menuitem"
              >
                <FaFileCsv className="text-green-600" /> Export as CSV
              </button>
              <button
                onClick={() => {
                  exportToPDF();
                  setShowExportDropdown(false);
                }}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                role="menuitem"
              >
                <FaFilePdf className="text-red-600" /> Export as PDF
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getUniqueValues = (key) => {
    return [...new Set(trucks.map(truck => truck[key]))].filter(Boolean);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      vehicleNumber: "",
      fullAddress: "",
      makerModel: "",
      engineNo: "",
      ownerName: "",
      registeredRTO: ""
    });
    setSearchTerm("");
    setCurrentPage(1);
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some(value => value !== "") || searchTerm !== "";
  };

  const activeFilterCount = () => {
    return Object.values(filters).filter(value => value !== "").length + (searchTerm ? 1 : 0);
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc" ? "desc" : "asc",
    }));
  };

  const filteredAndSortedTrucks = useMemo(() => {
    let filtered = [...trucks];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(truck =>
        Object.entries(truck).some(([_, value]) =>
          String(value).toLowerCase().includes(searchLower)
        )
      );
    }

    filtered = filtered.filter(truck => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        const truckValue = String(truck[key] || "").toLowerCase();
        return truckValue.includes(value.toLowerCase());
      });
    });

    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [trucks, searchTerm, sortConfig, filters]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTrucks = filteredAndSortedTrucks.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAndSortedTrucks.length / itemsPerPage);

  const handleTruckSelection = (truckId) => {
    setSelectedTrucks(prev => {
      if (prev.includes(truckId)) {
        return prev.filter(id => id !== truckId);
      }
      return [...prev, truckId];
    });
  };

  const handleSelectAll = () => {
    if (selectedTrucks.length === currentTrucks.length) {
      setSelectedTrucks([]);
    } else {
      setSelectedTrucks(currentTrucks.map(truck => truck._id));
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen mt-10">
      <div className="mb-6 bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Truck List</h1>
          <div className="flex items-center gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search in all fields..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-64 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${hasActiveFilters()
                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              <FaFilter />
              Filters
              {activeFilterCount() > 0 && (
                <span className="ml-1 px-2 py-0.5 text-sm bg-blue-500 text-white rounded-full">
                  {activeFilterCount()}
                </span>
              )}
            </button>
            <ExportDropdown />
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 border-t pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {filterFields.map(({ key, label }) => (
                <div key={key} className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    {label}
                  </label>
                  <input
                    type="text"
                    value={filters[key]}
                    onChange={(e) => handleFilterChange(key, e.target.value)}
                    placeholder={`Filter by ${label.toLowerCase()}`}
                    className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  />
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              {hasActiveFilters() && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-800 transition-colors"
                >
                  <FaSync className="w-4 h-4" /> Clear All Filters
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading trucks...</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-md overflow-hidden mb-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedTrucks.length === currentTrucks.length}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    {filterFields.map(({ key, label }) => (
                      <th
                        key={key}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort(key)}
                      >
                        <div className="flex items-center gap-2">
                          {label}
                          {sortConfig.key === key && (
                            sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {currentTrucks.map((truck) => (
              <div key={truck._id} className="bg-white shadow-md rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200">
                <div className="p-4">
                  <div className="flex items-center gap-4">
                    <input
                      type="checkbox"
                      checked={selectedTrucks.includes(truck._id)}
                      onChange={() => handleTruckSelection(truck._id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <VehicleTable data={truck} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex justify-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-4 py-2 border rounded-lg transition-colors ${currentPage === page
                    ? 'bg-blue-600 text-white'
                    : 'hover:bg-gray-50'
                    }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors"
              >
                Next
              </button>
            </div>
          )}

          {filteredAndSortedTrucks.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">No trucks found matching your filters.</p>
              {hasActiveFilters() && (
                <button
                  onClick={clearFilters}
                  className="mt-2 text-blue-600 hover:text-blue-800"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TruckList;
